import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  finalize,
  forkJoin,
  map,
  Observable,
  of,
  startWith,
  Subscription,
} from 'rxjs';
import {
  HrAlertService,
  HrHttpService,
  BudgetReportRow,
  ManpowerBudgetListItem,
  SaveCampusBudgetPayload,
} from '../hr-http.service';
import {
  BUDGET_FORM_ENABLED,
  canAccessBudgetForm,
  formatMrfAmountWithCommas,
  parseMrfAmountNumber,
  syncMrfAmountControlFromInput,
} from '../mrf-view-options';

@Component({
  selector: 'app-budget-details-form',
  templateUrl: './budget-details-form.component.html',
  styleUrls: ['./budget-details-form.component.scss'],
})
export class BudgetDetailsFormComponent implements OnInit, OnDestroy {
  private static readonly MIN_MONTHLY_SALARY = 5000;
  private static readonly MIN_BUDGET_AMOUNT = 15000;

  /** Search filters (inside mat-select panel) */
  campus = new FormControl('');
  teachingType = new FormControl('');
  department = new FormControl('');
  designation = new FormControl('');
  subject = new FormControl('');

  /** Selected values (mat-select) */
  campus2 = new FormControl('');
  fusionCampusCode = new FormControl('');
  teachingType2 = new FormControl('');
  department2 = new FormControl('');
  designation2 = new FormControl<string | number>('');
  subject2 = new FormControl('');

  budgetAmount = new FormControl('');
  employeeCount = new FormControl('');
  minSalary = new FormControl('');
  maxSalary = new FormControl('');
  avgSalary = new FormControl('');
  budgetYear = new FormControl('2026-27');
  remarks = new FormControl('');

  minSalaryError = '';
  maxSalaryError = '';
  avgSalaryError = '';
  budgetAmountError = '';
  employeeCountError = '';

  campusOptions = of<any[]>([]);
  teachingTypeOptions = of<any[]>([]);
  departmentOptions = of<any[]>([]);
  designationOptions = of<any[]>([]);
  subjectOptions = of<any[]>([]);

  private campusOptionsList: any[] = [];
  private teachingTypeOptionsList: any[] = [];
  private departmentOptionsList: any[] = [];
  private designationOptionsList: any[] = [];
  private subjectOptionsList: any[] = [];

  private readonly listsVersion$ = new BehaviorSubject(0);
  private selectedTeachingTypeId: number | string | null = null;
  private lastSelectedDepartment = '';
  private readonly subs = new Subscription();
  isSaving = false;
  /** Show Subject by default; hide when designation.subjetdesignation is false. */
  showSubjectSelection = true;
  hasBudgetFormAccess = false;
  private pendingEditRow: BudgetReportRow | null = null;
  /** Set when opened from budget report Edit — sent as campusBudgetId on save. */
  editingCampusBudgetId: number | null = null;
  /** Budget amount can change only when no employees are allotted to the designation. */
  canEditBudgetAmount = true;
  budgetAmountLockHint = '';
  private originalBudgetAmount: number | null = null;
  originalEmployeeCount: number | null = null;
  private campusBudgetReportRows: BudgetReportRow[] = [];
  private campusBudgetLockCampusId: number | null = null;

  get isEditingBudget(): boolean {
    return this.editingCampusBudgetId != null;
  }

  get organizationDesignationLabel(): string {
    const desig = this.findDesignationById(this.designation2.value);
    return String(desig?.desigName ?? desig?.designation ?? this.designation2.value ?? '').trim();
  }

  constructor(
    private hrHttpService: HrHttpService,
    private spinner: NgxSpinnerService,
    private hrAlert: HrAlertService,
    private router: Router
  ) {
    const navState = this.router.getCurrentNavigation()?.extras?.state as
      | { budgetEditRow?: BudgetReportRow }
      | undefined;
    this.pendingEditRow = navState?.budgetEditRow ?? null;
  }

  ngOnInit(): void {
    this.subs.add(
      this.hrHttpService.getUserRoles().pipe(catchError(() => of(null))).subscribe((roles) => {
        const permissions = roles
          ? this.hrHttpService.setMrfHrmsPermissionsFromResponse(roles)
          : this.hrHttpService.getMrfHrmsPermissions();
        this.hasBudgetFormAccess = canAccessBudgetForm(permissions);
        if (!BUDGET_FORM_ENABLED || !this.hasBudgetFormAccess) {
          this.hrAlert.show(
            BUDGET_FORM_ENABLED
              ? 'No permission to create budget.'
              : 'Budget form is currently disabled.',
            'Warning'
          );
          void this.router.navigate(['/hrDetails/budgetReport']);
          return;
        }
        if (!this.pendingEditRow) {
          const historyRow = history.state?.['budgetEditRow'] as BudgetReportRow | undefined;
          this.pendingEditRow = historyRow ?? null;
        }
        this.initFilteredOptions();
        this.bindCascades();
        this.loadMasterData();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  /** Digits only for counts; optional comma grouping for amount fields. */
  onNumericInput(
    control: FormControl,
    event: Event,
    formatAmount = false,
    allowNegative = false
  ): void {
    if (formatAmount) {
      syncMrfAmountControlFromInput(control, event, undefined, allowNegative);
      this.validateSalaryAgainstBudget();
      this.validateBudgetAmountAndEmpCount();
      return;
    }

    const input = event.target as HTMLInputElement | null;
    if (!input) {
      return;
    }
    const raw = String(input.value ?? '');
    const sign = allowNegative && raw.trim().startsWith('-') ? '-' : '';
    const digits = raw.replace(/\D/g, '');
    const next = sign + digits;
    if (input.value !== next) {
      input.value = next;
    }
    if (String(control.value ?? '') !== next) {
      control.setValue(next, { emitEvent: true });
    }
    this.validateSalaryAgainstBudget();
    this.validateBudgetAmountAndEmpCount();
  }

  fillBlankSalaryFields(): void {
    [this.minSalary, this.maxSalary, this.avgSalary].forEach((control) => {
      if (String(control.value ?? '').trim() === '') {
        this.applySalaryAmount(control, 0);
      }
    });
    this.validateSalaryAgainstBudget();
  }

  get budgetAmountFieldLabel(): string {
    return this.isEditingBudget ? 'Increase / Decrease Amount' : 'Budget Amount';
  }

  get budgetAmountPlaceholder(): string {
    return this.isEditingBudget
      ? 'Enter increase amount or -decrease amount'
      : 'Enter monthly budget amount';
  }

  get budgetAmountChangeTone(): 'up' | 'down' | 'same' | '' {
    const entered = this.parseAmountControlValue(this.budgetAmount.value, true);
    if (Number.isNaN(entered)) {
      return '';
    }
    if (this.isEditingBudget) {
      if (entered > 0) {
        return 'up';
      }
      if (entered < 0) {
        return 'down';
      }
      return this.originalBudgetAmount == null ? '' : 'same';
    }
    if (this.originalBudgetAmount == null) {
      return '';
    }
    if (entered > this.originalBudgetAmount) {
      return 'up';
    }
    if (entered < this.originalBudgetAmount) {
      return 'down';
    }
    return 'same';
  }

  get budgetAmountChangeLabel(): string {
    const tone = this.budgetAmountChangeTone;
    if (!tone) {
      return '';
    }
    const entered = this.parseAmountControlValue(this.budgetAmount.value, true);
    if (this.isEditingBudget) {
      const current = this.originalBudgetAmount ?? 0;
      const next = current + entered;
      if (tone === 'up') {
        return `Increasing by ${formatMrfAmountWithCommas(entered)}. New budget: ${formatMrfAmountWithCommas(next, true)}`;
      }
      if (tone === 'down') {
        return `Decreasing by ${formatMrfAmountWithCommas(Math.abs(entered))}. New budget: ${formatMrfAmountWithCommas(next, true)}`;
      }
      return `No change. Current budget: ${formatMrfAmountWithCommas(current, true)}`;
    }
    const diff = Math.abs(entered - (this.originalBudgetAmount ?? 0));
    if (tone === 'up') {
      return `Increasing by ${formatMrfAmountWithCommas(diff)}`;
    }
    if (tone === 'down') {
      return `Decreasing by ${formatMrfAmountWithCommas(diff)}`;
    }
    return 'Same as current budget';
  }

  private parseAmountControlValue(value: unknown, allowNegative = false): number {
    return parseMrfAmountNumber(value, allowNegative) ?? NaN;
  }

  private resolveEffectiveBudgetAmount(entered: number): number {
    if (this.isEditingBudget) {
      return (this.originalBudgetAmount ?? 0) + entered;
    }
    return entered;
  }

  private resolveEffectiveEmployeeCount(entered: number): number {
    if (this.isEditingBudget) {
      return (this.originalEmployeeCount ?? 0) + entered;
    }
    return entered;
  }

  private maxBudgetDecreaseOnEdit(): number {
    return Math.max(0, (this.originalBudgetAmount ?? 0) - BudgetDetailsFormComponent.MIN_BUDGET_AMOUNT);
  }

  private maxEmployeeDecreaseOnEdit(): number {
    return Math.max(0, (this.originalEmployeeCount ?? 0) - 1);
  }

  private parseEmployeeCountValue(value: unknown): number {
    const raw = String(value ?? '').trim();
    if (raw === '' || raw === '-') {
      return NaN;
    }
    if (!/^-?\d+$/.test(raw)) {
      return NaN;
    }
    return Number(raw);
  }

  validateBudgetAmountAndEmpCount(): void {
    const amountRaw = String(this.budgetAmount.value ?? '').trim();
    const empRaw = String(this.employeeCount.value ?? '').trim();
    const entered = amountRaw === '' ? NaN : this.parseAmountControlValue(amountRaw, true);
    const empCount = empRaw === '' ? NaN : this.parseEmployeeCountValue(empRaw);

    if (amountRaw === '' || Number.isNaN(entered)) {
      this.budgetAmountError = '';
    } else if (this.isEditingBudget) {
      const maxDecrease = this.maxBudgetDecreaseOnEdit();
      const effective = this.resolveEffectiveBudgetAmount(entered);
      if (entered < 0 && Math.abs(entered) > maxDecrease) {
        this.budgetAmountError = `Negative value cannot exceed -${formatMrfAmountWithCommas(
          maxDecrease
        )}. Remaining budget must be at least ${formatMrfAmountWithCommas(
          BudgetDetailsFormComponent.MIN_BUDGET_AMOUNT
        )}.`;
      } else if (effective < BudgetDetailsFormComponent.MIN_BUDGET_AMOUNT) {
        this.budgetAmountError = `Remaining budget must be at least ${formatMrfAmountWithCommas(
          BudgetDetailsFormComponent.MIN_BUDGET_AMOUNT
        )}.`;
      } else {
        this.budgetAmountError = '';
      }
    } else {
      const effective = this.resolveEffectiveBudgetAmount(entered);
      this.budgetAmountError =
        effective > BudgetDetailsFormComponent.MIN_BUDGET_AMOUNT
          ? ''
          : `Budget Amount must be greater than ${formatMrfAmountWithCommas(
              BudgetDetailsFormComponent.MIN_BUDGET_AMOUNT
            )}.`;
    }

    if (empRaw === '' || Number.isNaN(empCount)) {
      this.employeeCountError = '';
    } else if (this.isEditingBudget) {
      const maxDecrease = this.maxEmployeeDecreaseOnEdit();
      const effective = this.resolveEffectiveEmployeeCount(empCount);
      if (empCount < 0 && Math.abs(empCount) > maxDecrease) {
        this.employeeCountError = `Negative value cannot exceed -${maxDecrease}. Remaining positions must be at least 1.`;
      } else if (effective < 1) {
        this.employeeCountError = 'Remaining positions must be at least 1.';
      } else {
        this.employeeCountError = '';
      }
    } else {
      this.employeeCountError = empCount > 0 ? '' : 'Employee Count must be greater than 0.';
    }
  }

  /** Min/max/avg are optional. When entered, min >= 5000, max > min, min < avg < max. */
  validateSalaryAgainstBudget(): void {
    const minRaw = String(this.minSalary.value ?? '').trim();
    const maxRaw = String(this.maxSalary.value ?? '').trim();
    const avgRaw = String(this.avgSalary.value ?? '').trim();

    const min = minRaw === '' ? NaN : this.parseAmountControlValue(minRaw);
    const max = maxRaw === '' ? NaN : this.parseAmountControlValue(maxRaw);
    const avg = avgRaw === '' ? NaN : this.parseAmountControlValue(avgRaw);

    if (
      (minRaw === '' && maxRaw === '' && avgRaw === '') ||
      (min === 0 && max === 0 && avg === 0)
    ) {
      this.minSalaryError = '';
      this.maxSalaryError = '';
      this.avgSalaryError = '';
      return;
    }

    this.minSalaryError = this.resolveMinSalaryError(minRaw, min);
    this.maxSalaryError = this.resolveMaxSalaryError(maxRaw, max, minRaw, min);
    this.avgSalaryError = this.resolveAvgSalaryError(avgRaw, avg, minRaw, min, maxRaw, max);
  }

  private resolveMinSalaryError(raw: string, value: number): string {
    if (raw === '' || Number.isNaN(value)) {
      return '';
    }
    if (value < BudgetDetailsFormComponent.MIN_MONTHLY_SALARY) {
      return `Min Salary must be at least ${formatMrfAmountWithCommas(
        BudgetDetailsFormComponent.MIN_MONTHLY_SALARY
      )} per month.`;
    }
    return '';
  }

  private resolveMaxSalaryError(
    maxRaw: string,
    max: number,
    minRaw: string,
    min: number
  ): string {
    if (maxRaw === '' || Number.isNaN(max)) {
      return '';
    }
    if (minRaw !== '' && !Number.isNaN(min) && max <= min) {
      return 'Max Salary must be greater than Min Salary.';
    }
    return '';
  }

  private resolveAvgSalaryError(
    avgRaw: string,
    avg: number,
    minRaw: string,
    min: number,
    maxRaw: string,
    max: number
  ): string {
    if (avgRaw === '' || Number.isNaN(avg)) {
      return '';
    }
    if (minRaw !== '' && !Number.isNaN(min) && avg <= min) {
      return 'Avg Salary must be greater than Min Salary.';
    }
    if (maxRaw !== '' && !Number.isNaN(max) && avg >= max) {
      return 'Avg Salary must be less than Max Salary.';
    }
    return '';
  }

  trackByCampusId(_index: number, item: any): string | number {
    return item?.campusId ?? item?.campusName ?? _index;
  }

  /** Keep the options panel flush with the field (same left + width). */
  alignSelectPanel(opened: boolean, origin: HTMLElement): void {
    if (!opened || !origin) {
      return;
    }
    const pin = (): void => {
      const panel = document.querySelector('.cdk-overlay-pane .bdf-select-panel');
      const pane = panel?.closest('.cdk-overlay-pane') as HTMLElement | null;
      if (!pane) {
        return;
      }
      const rect = origin.getBoundingClientRect();
      pane.style.position = 'fixed';
      pane.style.left = `${rect.left}px`;
      pane.style.top = `${rect.bottom + 2}px`;
      pane.style.width = `${rect.width}px`;
      pane.style.minWidth = `${rect.width}px`;
      pane.style.maxWidth = `${rect.width}px`;
      pane.style.right = 'auto';
      pane.style.transform = 'none';
    };
    requestAnimationFrame(() => {
      pin();
      requestAnimationFrame(pin);
    });
    setTimeout(pin, 0);
    setTimeout(pin, 40);
  }

  onCampusSelectionChange(event: MatSelectChange): void {
    const selected = String(event.value ?? '').trim();
    this.campus.setValue(selected, { emitEvent: false });
    const campusRow = this.campusOptionsList.find(
      (c) => String(c?.campusName ?? '').trim() === selected
    );
    const code = String(
      campusRow?.fusionCampusCode ??
        campusRow?.campusCode ??
        campusRow?.fusionCode ??
        campusRow?.code ??
        ''
    ).trim();
    this.fusionCampusCode.setValue(code);
    this.clearBudgetDetailsIfCreating();
    this.refreshBudgetAmountLock();
  }

  goToBudgetReport(): void {
    void this.router.navigate(['/hrDetails/budgetReport']);
  }

  onTeachingTypeSelectionChange(event: MatSelectChange): void {
    const selected = String(event.value ?? '').trim();
    if (!selected) {
      this.selectedTeachingTypeId = null;
      this.clearDepartmentAndDesignation();
      this.clearBudgetDetailsIfCreating();
      return;
    }

    const campusSelected = String(this.campus2.value ?? '').trim();
    if (!campusSelected) {
      this.hrAlert.show('Please select Campus Name before Teaching Type.', 'Warning');
      this.teachingType.setValue('');
      this.teachingType2.setValue('', { emitEvent: false });
      this.selectedTeachingTypeId = null;
      this.clearDepartmentAndDesignation();
      this.clearBudgetDetailsIfCreating();
      return;
    }

    const teachType = this.teachingTypeOptionsList.find(
      (t) => this.hrHttpService.getMrfTeachTypeLabel(t) === selected
    );
    const typeId = teachType?.typeId;
    if (typeId == null) {
      this.selectedTeachingTypeId = null;
      this.clearDepartmentAndDesignation();
      this.clearBudgetDetailsIfCreating();
      return;
    }
    this.selectedTeachingTypeId = typeId;
    this.clearBudgetDetailsIfCreating();
    this.loadTypeWiseDepartmentsAndDesignations(typeId);
  }

  resetForm(): void {
    this.campus.setValue('');
    this.campus2.setValue('', { emitEvent: false });
    this.fusionCampusCode.setValue('');
    this.teachingType.setValue('');
    this.teachingType2.setValue('', { emitEvent: false });
    this.selectedTeachingTypeId = null;
    this.clearDepartmentAndDesignation();
    this.budgetAmount.setValue('');
    this.employeeCount.setValue('');
    this.minSalary.setValue('');
    this.maxSalary.setValue('');
    this.avgSalary.setValue('');
    this.budgetYear.setValue('2026-27');
    this.remarks.setValue('');
    this.editingCampusBudgetId = null;
    this.campusBudgetReportRows = [];
    this.campusBudgetLockCampusId = null;
    this.originalBudgetAmount = null;
    this.originalEmployeeCount = null;
    this.setBudgetAmountEditable(true);
    this.minSalaryError = '';
    this.maxSalaryError = '';
    this.avgSalaryError = '';
    this.budgetAmountError = '';
    this.employeeCountError = '';
  }

  submitBudget(): void {
    if (this.isSaving) {
      return;
    }

    this.fillBlankSalaryFields();

    const campus = String(this.campus2.value ?? '').trim();
    const fusionCampusCode = String(this.fusionCampusCode.value ?? '').trim();
    const teachingType = String(this.teachingType2.value ?? '').trim();
    const department = String(this.department2.value ?? '').trim();
    const selectedDesigId = this.toNumber(this.designation2.value);
    const subject = String(this.subject2.value ?? '').trim();
    const budgetAmount = String(this.budgetAmount.value ?? '').trim();
    const employeeCount = String(this.employeeCount.value ?? '').trim();
    const minSalary = String(this.minSalary.value ?? '').trim();
    const maxSalary = String(this.maxSalary.value ?? '').trim();
    const avgSalary = String(this.avgSalary.value ?? '').trim();
    const budgetYear = String(this.budgetYear.value ?? '').trim();
    const remarks = String(this.remarks.value ?? '').trim();

    const missing: string[] = [];
    if (!campus) missing.push('Campus Name');
    if (!fusionCampusCode) missing.push('Fusion Campus Code');
    if (!teachingType) missing.push('Teaching Type');
    if (!department) missing.push('Department');
    if (selectedDesigId == null) missing.push('Designation');
    if (!budgetAmount) missing.push('Budget Amount');
    if (!employeeCount) missing.push('Employee Count');

    if (missing.length) {
      this.hrAlert.show(`Please fill: ${missing.join(', ')}.`, 'Warning');
      return;
    }

    const min = minSalary === '' ? 0 : this.parseAmountControlValue(minSalary);
    const max = maxSalary === '' ? 0 : this.parseAmountControlValue(maxSalary);
    const avg = avgSalary === '' ? 0 : this.parseAmountControlValue(avgSalary);
    const amount = this.parseAmountControlValue(budgetAmount, true);
    const empCount = this.parseEmployeeCountValue(employeeCount);
    const salaryEntered =
      (minSalary !== '' && min !== 0) ||
      (maxSalary !== '' && max !== 0) ||
      (avgSalary !== '' && avg !== 0);
    if (
      Number.isNaN(amount) ||
      Number.isNaN(empCount) ||
      (minSalary !== '' && Number.isNaN(min)) ||
      (maxSalary !== '' && Number.isNaN(max)) ||
      (avgSalary !== '' && Number.isNaN(avg))
    ) {
      this.hrAlert.show('Please enter valid numeric values for budget fields.', 'Warning');
      return;
    }
    const effectiveBudgetAmount = this.resolveEffectiveBudgetAmount(amount);
    const effectiveEmpCount = this.resolveEffectiveEmployeeCount(empCount);
    if (this.isEditingBudget) {
      if (effectiveBudgetAmount < BudgetDetailsFormComponent.MIN_BUDGET_AMOUNT) {
        this.hrAlert.show(
          `Remaining budget must be at least ${formatMrfAmountWithCommas(
            BudgetDetailsFormComponent.MIN_BUDGET_AMOUNT
          )}.`,
          'Warning'
        );
        return;
      }
      if (effectiveEmpCount < 1) {
        this.hrAlert.show('Remaining positions must be at least 1.', 'Warning');
        return;
      }
    } else if (effectiveBudgetAmount <= BudgetDetailsFormComponent.MIN_BUDGET_AMOUNT) {
      this.hrAlert.show(
        `Budget Amount must be greater than ${formatMrfAmountWithCommas(
          BudgetDetailsFormComponent.MIN_BUDGET_AMOUNT
        )}.`,
        'Warning'
      );
      return;
    } else if (empCount <= 0) {
      this.hrAlert.show('Employee Count must be greater than 0.', 'Warning');
      return;
    }
    this.validateSalaryAgainstBudget();
    this.validateBudgetAmountAndEmpCount();
    if (this.minSalaryError || this.maxSalaryError || this.avgSalaryError) {
      this.hrAlert.show('Please fix salary errors before submitting.', 'Warning');
      return;
    }
    if (this.budgetAmountError || this.employeeCountError) {
      this.hrAlert.show('Please fix budget amount and employee count errors before submitting.', 'Warning');
      return;
    }
    if (salaryEntered && minSalary !== '' && maxSalary !== '' && min >= max) {
      this.hrAlert.show('Max Salary must be greater than Min Salary.', 'Warning');
      return;
    }
    if (
      salaryEntered &&
      minSalary !== '' &&
      maxSalary !== '' &&
      avgSalary !== '' &&
      (avg <= min || avg >= max)
    ) {
      this.hrAlert.show(
        'Avg Salary must be greater than Min Salary and less than Max Salary.',
        'Warning'
      );
      return;
    }

    const campusRow = this.campusOptionsList.find(
      (c) => String(c?.campusName ?? '').trim() === campus
    );
    const campusId = this.toNumber(
      campusRow?.campusId ?? campusRow?.id ?? campusRow?.campus_id
    );

    const deptRow = this.departmentOptionsList.find(
      (d) => String(d?.deptName ?? d?.departmentName ?? d?.name ?? '').trim() === department
    );
    const deptId = this.toNumber(deptRow?.deptId ?? deptRow?.departmentId ?? deptRow?.id);

    const desigRow = this.findDesignationById(selectedDesigId);
    const desigId = this.toNumber(desigRow?.desigId ?? desigRow?.designationId ?? desigRow?.id);

    const subjectRow = subject
      ? this.subjectOptionsList.find(
          (s) => String(s?.subName ?? s?.subjectName ?? '').trim() === subject
        )
      : null;
    const subjectId = subject
      ? this.toNumber(subjectRow?.subId ?? subjectRow?.subjectId ?? subjectRow?.id)
      : null;

    if (campusId == null) {
      this.hrAlert.show('Unable to resolve Campus Id. Please reselect campus.', 'Warning');
      return;
    }
    const teachTypeId =
      this.toNumber(this.selectedTeachingTypeId) ??
      this.toNumber(
        this.teachingTypeOptionsList.find(
          (t) => this.hrHttpService.getMrfTeachTypeLabel(t) === teachingType
        )?.typeId
      );
    if (teachTypeId == null) {
      this.hrAlert.show('Unable to resolve Teaching Type Id. Please reselect teaching type.', 'Warning');
      return;
    }
    if (deptId == null) {
      this.hrAlert.show('Unable to resolve Department Id. Please reselect department.', 'Warning');
      return;
    }
    if (desigId == null) {
      this.hrAlert.show('Unable to resolve Designation Id. Please reselect designation.', 'Warning');
      return;
    }
    if (subject && subjectId == null) {
      this.hrAlert.show('Unable to resolve Subject Id. Please reselect subject.', 'Warning');
      return;
    }

    const budgetRow: ManpowerBudgetListItem = {
      fusionCampusCode,
      teachTypeId,
      deptId,
      desigId,
      subjectId,
      budgetAmount: amount,
      empCount,
      minSalary: min,
      maxSalary: max,
      avgSalary: avg,
    };
    const payload: SaveCampusBudgetPayload = {
      campusId,
      budgetYear,
      remarks,
      manpowerBudgetList: [budgetRow],
    };
    if (this.editingCampusBudgetId != null) {
      payload.campusBudgetId = this.editingCampusBudgetId;
    }

    this.isSaving = true;
    this.spinner.show();
    this.hrHttpService
      .saveCampusBudget(payload)
      .pipe(
        finalize(() => {
          this.isSaving = false;
          this.spinner.hide();
        })
      )
      .subscribe({
        next: (res) => {
          const message = this.extractSuccessMessage(res);
          if (!message) {
            this.hrAlert.show('Budget saved, but no message was returned by the server.', 'Warning');
            this.resetForm();
            return;
          }
          this.hrAlert.show(message, 'Success');
          if (this.isEditingBudget) {
            void this.router.navigate(['/hrDetails/budgetReport']);
            return;
          }
          this.resetForm();
        },
        error: (err) => {
          console.error('saveCampusBudget failed', err);
          this.hrAlert.show(
            this.extractErrorMessage(err) || 'Unable to save budget. Please try again.',
            'Error'
          );
        },
      });
  }

  private toNumber(value: unknown): number | null {
    if (value == null || value === '') {
      return null;
    }
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }

  private extractSuccessMessage(res: unknown): string {
    if (res == null) {
      return '';
    }
    if (typeof res === 'string') {
      return res.trim().replace(/^"|"$/g, '');
    }
    if (typeof res !== 'object') {
      return String(res).trim();
    }
    const row = res as Record<string, unknown>;
    const data = row['data'];
    const dataObj =
      data && typeof data === 'object' && !Array.isArray(data)
        ? (data as Record<string, unknown>)
        : null;

    for (const key of ['message', 'msg', 'statusMessage', 'responseMessage']) {
      const value = row[key] ?? dataObj?.[key];
      if (value != null && String(value).trim()) {
        return String(value).trim();
      }
    }
    if (typeof data === 'string' && data.trim()) {
      return data.trim();
    }
    return '';
  }

  private extractErrorMessage(err: unknown): string {
    const httpErr = err as {
      error?: { message?: string; msg?: string; error?: string } | string;
      message?: string;
    };
    const body = httpErr?.error;
    if (typeof body === 'string' && body.trim()) {
      return body.trim();
    }
    if (body && typeof body === 'object') {
      for (const key of ['message', 'msg', 'error'] as const) {
        const value = body[key];
        if (value != null && String(value).trim()) {
          return String(value).trim();
        }
      }
    }
    return String(httpErr?.message ?? '').trim();
  }

  onDesignationSelectionChange(event: MatSelectChange): void {
    const desigId = event.value;
    const desig = this.findDesignationById(desigId);
    console.log('[BudgetForm] selected designation', desig ?? { desigId, notFound: true });
    this.updateSubjectVisibility(desigId);
    this.clearBudgetDetailsIfCreating();
    this.refreshBudgetAmountLock();
  }

  private clearBudgetDetailsIfCreating(): void {
    if (this.isEditingBudget) {
      return;
    }
    this.budgetAmount.setValue('');
    this.employeeCount.setValue('');
    this.minSalary.setValue('');
    this.maxSalary.setValue('');
    this.avgSalary.setValue('');
    this.remarks.setValue('');
    this.originalBudgetAmount = null;
    this.originalEmployeeCount = null;
    this.minSalaryError = '';
    this.maxSalaryError = '';
    this.avgSalaryError = '';
    this.budgetAmountError = '';
    this.employeeCountError = '';
  }

  private bindCascades(): void {
    this.subs.add(
      this.department2.valueChanges.subscribe((deptName) => {
        const normalized = String(deptName ?? '').trim();
        if (!normalized) {
          this.lastSelectedDepartment = '';
          this.clearDesignationFields();
          this.clearBudgetDetailsIfCreating();
          return;
        }
        const departmentChanged =
          this.lastSelectedDepartment !== '' && this.lastSelectedDepartment !== normalized;
        if (departmentChanged) {
          this.clearDesignationFields();
        }
        this.clearBudgetDetailsIfCreating();
        this.lastSelectedDepartment = normalized;
        const dept = this.departmentOptionsList.find(
          (d) => (d?.deptName ?? d?.departmentName ?? d?.name) === normalized
        );
        if (dept) {
          this.setDesignationListForDepartment(dept);
        }
      })
    );
    this.subs.add(
      this.designation2.valueChanges.subscribe((desigId) => {
        this.updateSubjectVisibility(desigId);
        this.clearBudgetDetailsIfCreating();
        this.refreshBudgetAmountLock();
      })
    );
    this.subs.add(
      this.subject2.valueChanges.subscribe(() => {
        this.clearBudgetDetailsIfCreating();
        this.refreshBudgetAmountLock();
      })
    );
  }

  /** On each designation select: show Subject only when that row's subjetdesignation is true. */
  private updateSubjectVisibility(desigId: unknown): void {
    if (desigId == null || desigId === '') {
      this.showSubjectSelection = true;
      this.clearSubjectFields();
      return;
    }
    const desig = this.findDesignationById(desigId);
    if (!desig) {
      this.showSubjectSelection = true;
      this.clearSubjectFields();
      return;
    }
    this.showSubjectSelection = this.isSubjectDesignationRequired(desig);
    if (!this.showSubjectSelection) {
      this.clearSubjectFields();
    }
  }

  private findDesignationById(desigId: unknown): any | undefined {
    const id = this.toNumber(desigId);
    if (id == null) {
      return undefined;
    }
    return this.designationOptionsList.find((d) => {
      const rowId = this.toNumber(d?.desigId ?? d?.designationId ?? d?.id);
      return rowId === id;
    });
  }

  trackByDesigId(_index: number, item: any): string | number {
    return item?.desigId ?? item?.id ?? _index;
  }

  private isSubjectDesignationRequired(desig: any): boolean {
    const flag =
      desig?.subjetdesignation ??
      desig?.subjectDesignation ??
      desig?.subjectdesignation ??
      desig?.isSubjectDesignation ??
      desig?.subjectRequired;
    if (flag == null || flag === '') {
      return true;
    }
    if (flag === false || flag === 0 || flag === 'false' || flag === 'N' || flag === 'n') {
      return false;
    }
    return flag === true || flag === 1 || flag === 'true' || flag === 'Y' || flag === 'y';
  }

  private clearSubjectFields(): void {
    this.subject.setValue('');
    this.subject2.setValue('', { emitEvent: false });
  }

  private loadMasterData(): void {
    this.spinner.show();
    forkJoin({
      campuses: this.safeApiCall(this.hrHttpService.getEmpCampuses()),
      basicInfo: this.safeApiCall(this.hrHttpService.getMrfBasicInfo()),
      teachTypes: this.safeApiCall(this.hrHttpService.getMrfTeachTypes()),
    })
      .pipe(finalize(() => this.spinner.hide()))
      .subscribe({
        next: ({ campuses, basicInfo, teachTypes }) => {
          this.campusOptionsList = (campuses as any)?.data ?? campuses ?? [];
          const basic = (basicInfo as any)?.data ?? basicInfo ?? {};
          this.subjectOptionsList = this.normalizeSubjects(basic?.subjects ?? []);
          this.teachingTypeOptionsList =
            this.hrHttpService.parseMrfTeachTypesResponse(teachTypes);
          this.refreshFilterStreams();
          if (this.pendingEditRow) {
            const editRow = this.pendingEditRow;
            this.pendingEditRow = null;
            this.applyEditFromReportRow(editRow);
          }
        },
        error: (err) => console.error(err),
      });
  }

  private loadTypeWiseDepartmentsAndDesignations(typeId: number | string): void {
    this.spinner.show();
    this.hrHttpService.getMrfTypeWiseDesigAndDepartments(typeId).subscribe({
      next: (res: any) => {
        const data = res?.data ?? res;
        const rawList = Array.isArray(data) ? data : [];
        this.departmentOptionsList = this.normalizeDepartments(rawList);
        this.department.setValue('');
        this.department2.setValue('', { emitEvent: false });
        this.clearDesignationFields();
        this.refreshFilterStreams();
        this.spinner.hide();
      },
      error: (err) => {
        this.spinner.hide();
        console.error(err);
      },
    });
  }

  private clearDepartmentAndDesignation(): void {
    this.departmentOptionsList = [];
    this.department.setValue('');
    this.department2.setValue('', { emitEvent: false });
    this.lastSelectedDepartment = '';
    this.clearDesignationFields();
  }

  private clearDesignationFields(): void {
    this.designationOptionsList = [];
    this.designation.setValue('');
    this.designation2.setValue('', { emitEvent: false });
    this.showSubjectSelection = true;
    this.clearSubjectFields();
    this.refreshFilterStreams();
  }

  private setDesignationListForDepartment(dept: any): void {
    this.designationOptionsList = this.normalizeDesignationList(
      dept?.designations ?? dept?.desigDetails ?? []
    );
    this.designation.setValue('');
    this.designation2.setValue('', { emitEvent: false });
    this.showSubjectSelection = true;
    this.clearSubjectFields();
    this.refreshFilterStreams();
  }

  private initFilteredOptions(): void {
    this.campusOptions = combineLatest([
      this.campus.valueChanges.pipe(startWith(this.campus.value ?? '')),
      this.listsVersion$,
    ]).pipe(
      map(([value]) =>
        this.filterList(this.asSearchText(value), this.campusOptionsList, ['campusName'])
      )
    );

    this.teachingTypeOptions = combineLatest([
      this.teachingType.valueChanges.pipe(startWith(this.teachingType.value ?? '')),
      this.listsVersion$,
    ]).pipe(
      map(([value]) =>
        this.filterList(this.asSearchText(value), this.teachingTypeOptionsList, [
          'roleType',
          'TeachType',
        ])
      )
    );

    this.departmentOptions = combineLatest([
      this.department.valueChanges.pipe(startWith(this.department.value ?? '')),
      this.listsVersion$,
    ]).pipe(
      map(([value]) =>
        this.filterList(this.asSearchText(value), this.departmentOptionsList, [
          'deptName',
          'departmentName',
          'name',
        ])
      )
    );

    this.designationOptions = combineLatest([
      this.designation.valueChanges.pipe(startWith(this.designation.value ?? '')),
      this.listsVersion$,
    ]).pipe(
      map(([value]) =>
        this.filterList(this.asSearchText(value), this.designationOptionsList, [
          'desigName',
          'designation',
        ])
      )
    );

    this.subjectOptions = combineLatest([
      this.subject.valueChanges.pipe(startWith(this.subject.value ?? '')),
      this.listsVersion$,
    ]).pipe(
      map(([value]) =>
        this.filterList(this.asSearchText(value), this.subjectOptionsList, [
          'subName',
          'subjectName',
        ])
      )
    );
  }

  private refreshFilterStreams(): void {
    this.listsVersion$.next(this.listsVersion$.value + 1);
  }

  private asSearchText(value: unknown): string {
    if (typeof value === 'string') {
      return value;
    }
    return '';
  }

  private filterList(value: string, list: any[], fields: string[]): any[] {
    const searchValue = value.trim().toLocaleLowerCase();
    const hasLabel = (item: any): boolean =>
      fields.some((field) => {
        const label = item?.[field];
        return label != null && String(label).trim() !== '';
      });

    return list.filter((item) => {
      if (!searchValue) {
        return hasLabel(item);
      }
      return fields.some((field) => {
        const label = item?.[field];
        return (
          label != null &&
          String(label).trim().toLocaleLowerCase().includes(searchValue)
        );
      });
    });
  }

  private normalizeDepartments(raw: unknown): any[] {
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw.map((item) => {
      if (typeof item === 'string') {
        return { deptName: item, designations: [], desigDetails: [] };
      }
      const deptName =
        item?.deptName ??
        item?.departmentName ??
        item?.deptname ??
        item?.name ??
        '';
      const designations =
        item?.designations ?? item?.desigDetails ?? item?.designationList ?? [];
      return { ...item, deptName: String(deptName), designations, desigDetails: designations };
    });
  }

  private normalizeDesignationList(raw: unknown): any[] {
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw.map((item) => {
      if (typeof item === 'string') {
        return { desigName: item, desigId: null, subjetdesignation: true };
      }
      const desigName =
        item?.desigName ?? item?.designation ?? item?.designame ?? item?.name ?? '';
      const subjectFlag =
        item?.subjetdesignation ??
        item?.subjectDesignation ??
        item?.subjectdesignation ??
        item?.isSubjectDesignation ??
        item?.subjectRequired;
      return {
        ...item,
        desigName: String(desigName),
        desigId: item?.desigId ?? item?.id ?? null,
        subjetdesignation: subjectFlag,
        subjectDesignation: subjectFlag,
      };
    });
  }

  private normalizeSubjects(raw: unknown): any[] {
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw.map((item) => {
      if (typeof item === 'string') {
        return { subName: item };
      }
      const subName =
        item?.subName ?? item?.subjectName ?? item?.subject ?? item?.name ?? '';
      return { ...item, subName: String(subName) };
    });
  }

  private safeApiCall<T>(obs: Observable<T>): Observable<T | null> {
    return obs.pipe(
      catchError((err) => {
        console.error(err);
        return of(null);
      })
    );
  }

  private applyEditFromReportRow(row: BudgetReportRow): void {
    const raw = row.raw ?? {};
    this.editingCampusBudgetId =
      row.budgetId ?? this.toNumber(raw['campusBudgetId'] ?? raw['budgetId'] ?? raw['id']);

    const campusId = this.toNumber(row.campusId ?? raw['campusId']);
    const campusName = String(row.campus ?? raw['campusName'] ?? '').trim();
    const fusionCode = String(raw['fusionCampusCode'] ?? raw['fusionCode'] ?? '').trim();
    const deptId = this.toNumber(raw['deptId']);
    const desigId = this.toNumber(raw['desigId']);
    const teachTypeId = this.toNumber(raw['teachTypeId'] ?? raw['typeId']);
    const { subjectId, subjectName } = this.extractEditSubjectFromRow(raw, row);
    const departmentName = String(row.department ?? raw['department'] ?? '').trim();
    const savedBudgetAmount = row.budgetAmount ?? this.toNumber(raw['budgetAmount']);
    this.rememberOriginalBudgetAmount(savedBudgetAmount);
    this.rememberOriginalEmployeeCount(this.toNumber(raw['empCount']));

    const budgetYear = String(raw['budgetYear'] ?? '').trim();
    if (budgetYear) {
      this.budgetYear.setValue(budgetYear, { emitEvent: false });
    }

    this.applyZeroBudgetEntryValues();
    this.validateSalaryAgainstBudget();
    this.validateBudgetAmountAndEmpCount();
    this.applyBudgetAmountLockFromRow(row);

    if (campusId == null && !campusName) {
      this.hrAlert.show('Unable to bind campus from selected budget row.', 'Warning');
      return;
    }

    this.ensureCampusOption(campusId, campusName, fusionCode);

    if (deptId == null || desigId == null) {
      this.applyZeroBudgetEntryValues();
      this.refreshFilterStreams();
      this.refreshBudgetAmountLock();
      return;
    }

    this.spinner.show();
    this.resolveTeachingTypeForEdit(deptId, desigId, teachTypeId)
      .pipe(finalize(() => this.spinner.hide()))
      .subscribe((match) => {
        if (!match) {
          if (departmentName) {
            this.department2.setValue(departmentName, { emitEvent: false });
            this.department.setValue(departmentName, { emitEvent: false });
          }
          if (desigId != null) {
            this.designation2.setValue(desigId as string | number, { emitEvent: false });
            this.updateSubjectVisibility(desigId);
          }
          this.bindEditSubject(subjectId, subjectName);
          this.applyZeroBudgetEntryValues();
          this.refreshFilterStreams();
          this.refreshBudgetAmountLock();
          return;
        }

        this.selectedTeachingTypeId = match.typeId;
        this.teachingType2.setValue(match.label, { emitEvent: false });
        this.teachingType.setValue(match.label, { emitEvent: false });
        this.departmentOptionsList = match.departments;

        const dept =
          match.departments.find(
            (d) => this.toNumber(d.deptId ?? d.departmentId ?? d.id) === deptId
          ) ??
          match.departments.find(
            (d) => String(d.deptName ?? d.departmentName ?? d.name ?? '').trim() === departmentName
          );

        const deptLabel = String(dept?.deptName ?? dept?.departmentName ?? departmentName).trim();
        if (deptLabel) {
          this.department2.setValue(deptLabel, { emitEvent: false });
          this.department.setValue(deptLabel, { emitEvent: false });
          this.lastSelectedDepartment = deptLabel;
        }

        if (dept) {
          this.setDesignationListForDepartment(dept);
        }

        this.designation2.setValue(desigId as string | number, { emitEvent: false });
        this.updateSubjectVisibility(desigId);
        this.bindEditSubject(subjectId, subjectName);

        this.applyZeroBudgetEntryValues();
        this.refreshFilterStreams();
        this.refreshBudgetAmountLock();
      });
  }

  private ensureCampusOption(
    campusId: number | null,
    campusName: string,
    fusionCodeHint = ''
  ): void {
    let campusRow = this.campusOptionsList.find(
      (c) => this.toNumber(c?.campusId ?? c?.id ?? c?.campus_id) === campusId
    );
    if (!campusRow && campusName) {
      campusRow = {
        campusId,
        campusName,
        name: campusName,
        fusionCampusCode: fusionCodeHint || undefined,
      };
      this.campusOptionsList = [...this.campusOptionsList, campusRow];
    }

    const name = String(campusRow?.campusName ?? campusName).trim();
    if (!name) {
      return;
    }

    this.campus2.setValue(name, { emitEvent: false });
    this.campus.setValue(name, { emitEvent: false });
    const code = String(
      fusionCodeHint ||
        campusRow?.fusionCampusCode ||
        campusRow?.campusCode ||
        campusRow?.fusionCode ||
        campusRow?.code ||
        campusId ||
        ''
    ).trim();
    this.fusionCampusCode.setValue(code);
  }

  private extractEditSubjectFromRow(
    raw: Record<string, unknown>,
    row: BudgetReportRow
  ): { subjectId: number | null; subjectName: string } {
    const subjectId = this.pickEditSubjectId(raw) ?? row.subjectId;
    let subjectName = String(
      raw['subjectName'] ?? raw['subName'] ?? raw['subject'] ?? row.subjectName ?? ''
    ).trim();
    if (subjectName === '—') {
      subjectName = '';
    }
    const subjectObj = raw['subject'];
    if (
      !subjectName &&
      subjectObj &&
      typeof subjectObj === 'object' &&
      !Array.isArray(subjectObj)
    ) {
      subjectName = String(
        (subjectObj as Record<string, unknown>)['subName'] ??
          (subjectObj as Record<string, unknown>)['subjectName'] ??
          (subjectObj as Record<string, unknown>)['name'] ??
          ''
      ).trim();
    }
    return { subjectId, subjectName };
  }

  private pickEditSubjectId(raw: Record<string, unknown>): number | null {
    const candidates: unknown[] = [
      raw['subjectId'],
      raw['subId'],
      raw['subject_id'],
      raw['subjectID'],
      raw['SubId'],
      raw['SubjectId'],
    ];
    const subjectObj = raw['subject'];
    if (subjectObj && typeof subjectObj === 'object' && !Array.isArray(subjectObj)) {
      const nested = subjectObj as Record<string, unknown>;
      candidates.push(nested['id'], nested['subId'], nested['subjectId']);
    }
    for (const candidate of candidates) {
      const id = this.toNumber(candidate);
      if (id != null && id !== 0) {
        return id;
      }
    }
    return null;
  }

  private bindEditSubject(subjectId: number | null, subjectName: string): void {
    if (subjectId == null && !subjectName.trim()) {
      return;
    }

    const label = this.resolveEditSubjectLabel(subjectId, subjectName);
    if (!label) {
      return;
    }

    this.showSubjectSelection = true;
    this.ensureSubjectOption(subjectId, label);
    this.setSubjectSelectPair(label);
    this.refreshFilterStreams();
  }

  /** Clear the in-panel search and set the mat-select value to a list option label. */
  private setSubjectSelectPair(value: string): void {
    const canonical = this.resolveCanonicalSubjectValue(value);
    if (!canonical) {
      return;
    }
    this.subject.setValue('', { emitEvent: false });
    this.subject2.setValue(canonical, { emitEvent: false });
  }

  private resolveEditSubjectLabel(subjectId: number | null, subjectName: string): string {
    if (subjectId != null) {
      const byId = this.findSubjectOptionById(subjectId);
      if (byId) {
        return this.getSubjectOptionLabel(byId);
      }
      const fromDesig = this.findSubjectNameById(subjectId);
      if (fromDesig) {
        return fromDesig;
      }
    }

    const trimmedName = subjectName.trim();
    if (trimmedName) {
      const byName = this.findSubjectOptionByName(trimmedName);
      if (byName) {
        return this.getSubjectOptionLabel(byName);
      }
      return trimmedName;
    }

    return '';
  }

  private resolveCanonicalSubjectValue(value: string): string {
    const normalized = value.trim().toLowerCase();
    if (!normalized) {
      return '';
    }
    const match = this.findSubjectOptionByName(value);
    if (match) {
      return this.getSubjectOptionLabel(match);
    }
    return value.trim();
  }

  private getSubjectOptionLabel(item: any): string {
    return String(item?.subName ?? item?.subjectName ?? item?.subject ?? '').trim();
  }

  private findSubjectOptionById(subjectId: number): any | undefined {
    return this.subjectOptionsList.find(
      (item) => String(item?.subId ?? item?.subjectId ?? item?.id ?? '') === String(subjectId)
    );
  }

  private findSubjectOptionByName(subjectName: string): any | undefined {
    const normalized = subjectName.trim().toLowerCase();
    if (!normalized) {
      return undefined;
    }
    return this.subjectOptionsList.find((item) => {
      const label = this.getSubjectOptionLabel(item).toLowerCase();
      return label === normalized;
    });
  }

  private findSubjectNameById(subjectId: number): string {
    const fromMaster = this.findSubjectOptionById(subjectId);
    const masterName = fromMaster ? this.getSubjectOptionLabel(fromMaster) : '';
    if (masterName) {
      return masterName;
    }

    const desig = this.findDesignationById(this.designation2.value);
    const desigSubjects = this.normalizeSubjects(
      desig?.subjects ?? desig?.subjectDetails ?? desig?.subjectList ?? []
    );
    const fromDesig = desigSubjects.find(
      (item) => String(item?.subId ?? item?.subjectId ?? item?.id ?? '') === String(subjectId)
    );
    const desigName = fromDesig ? this.getSubjectOptionLabel(fromDesig) : '';
    if (desigName) {
      this.ensureSubjectOption(subjectId, desigName);
    }
    return desigName;
  }

  private ensureSubjectOption(subjectId: number | null, subjectName: string): void {
    const existing =
      (subjectId != null ? this.findSubjectOptionById(subjectId) : undefined) ??
      this.findSubjectOptionByName(subjectName);
    if (existing) {
      return;
    }
    this.subjectOptionsList = [
      ...this.subjectOptionsList,
      {
        subId: subjectId,
        subjectId,
        subName: subjectName,
        subjectName,
      },
    ];
  }

  private refreshBudgetAmountLock(): void {
    if (!this.isEditingBudget) {
      this.originalBudgetAmount = null;
      this.campusBudgetReportRows = [];
      this.campusBudgetLockCampusId = null;
      this.setBudgetAmountEditable(true);
      return;
    }

    const campusId = this.resolveSelectedCampusId();
    const desigId = this.toNumber(this.designation2.value);
    if (campusId == null || desigId == null) {
      this.setBudgetAmountEditable(true);
      return;
    }

    if (this.campusBudgetLockCampusId === campusId && this.campusBudgetReportRows.length) {
      this.applyBudgetAmountLockFromRows(this.campusBudgetReportRows, campusId, desigId);
      return;
    }

    this.subs.add(
      this.hrHttpService
        .getCampusBudgetReportWithFilters({ campusId })
        .pipe(
          map((res) => this.hrHttpService.parseBudgetReportResponse(res).rows),
          catchError((err) => {
            console.error('getCampusBudgetReportWithFilters failed', err);
            return of([] as BudgetReportRow[]);
          })
        )
        .subscribe((rows) => {
          this.campusBudgetReportRows = rows;
          this.campusBudgetLockCampusId = campusId;
          this.applyBudgetAmountLockFromRows(rows, campusId, desigId);
        })
    );
  }

  private applyBudgetAmountLockFromRows(
    rows: BudgetReportRow[],
    campusId: number,
    desigId: number
  ): void {
    const match = this.findCampusBudgetRowForDesignation(rows, campusId, desigId);
    this.applyBudgetAmountLockFromRow(match);
  }

  private findCampusBudgetRowForDesignation(
    rows: BudgetReportRow[],
    campusId: number,
    desigId: number
  ): BudgetReportRow | null {
    const subjectId = this.resolveSelectedSubjectId();
    const sameCampus = rows.filter((row) => {
      const rowCampusId = row.campusId ?? this.toNumber(row.raw?.['campusId']);
      const rowDesigId = row.desigId ?? this.toNumber(row.raw?.['desigId']);
      return rowCampusId === campusId && rowDesigId === desigId;
    });
    if (!sameCampus.length) {
      return null;
    }
    if (subjectId != null) {
      const bySubject = sameCampus.find((row) => (row.subjectId ?? this.toNumber(row.raw?.['subjectId'])) === subjectId);
      if (bySubject) {
        return bySubject;
      }
    }
    return sameCampus[0];
  }

  private applyBudgetAmountLockFromRow(row: BudgetReportRow | null): void {
    if (!row) {
      this.setBudgetAmountEditable(true);
      return;
    }
    if (this.originalBudgetAmount == null) {
      this.rememberOriginalBudgetAmount(row.budgetAmount);
    }
    this.setBudgetAmountEditable(this.hrHttpService.isCampusBudgetAmountEditable(row));
  }

  private rememberOriginalBudgetAmount(amount: number | null): void {
    this.originalBudgetAmount = amount != null && Number.isFinite(amount) ? amount : null;
  }

  private rememberOriginalEmployeeCount(count: number | null): void {
    this.originalEmployeeCount = count != null && Number.isFinite(count) ? count : null;
  }

  private setBudgetAmountEditable(editable: boolean): void {
    this.canEditBudgetAmount = editable;
    this.budgetAmountLockHint = '';
    this.budgetAmount.enable({ emitEvent: false });
  }

  private resolveSelectedCampusId(): number | null {
    const campus = String(this.campus2.value ?? '').trim();
    const campusRow = this.campusOptionsList.find(
      (c) => String(c?.campusName ?? '').trim() === campus
    );
    return this.toNumber(campusRow?.campusId ?? campusRow?.id ?? campusRow?.campus_id);
  }

  private resolveSelectedSubjectId(): number | null {
    const subject = String(this.subject2.value ?? '').trim();
    if (!subject) {
      return null;
    }
    const subjectRow = this.subjectOptionsList.find(
      (s) => String(s?.subName ?? s?.subjectName ?? '').trim() === subject
    );
    return this.toNumber(subjectRow?.subId ?? subjectRow?.subjectId ?? subjectRow?.id);
  }

  private applyZeroBudgetEntryValues(): void {
    this.applyBudgetAmount(0);
    this.employeeCount.setValue('0', { emitEvent: false });
    this.applySalaryAmount(this.minSalary, 0);
    this.applySalaryAmount(this.maxSalary, 0);
    this.applySalaryAmount(this.avgSalary, 0);
    this.remarks.setValue('', { emitEvent: false });
  }

  private applyBudgetAmount(amount: number | null): void {
    if (amount == null) {
      return;
    }
    this.budgetAmount.setValue(formatMrfAmountWithCommas(amount, true), { emitEvent: false });
  }

  private applySalaryAmount(control: FormControl, value: unknown): void {
    const amount = this.toNumber(value);
    if (amount == null) {
      return;
    }
    control.setValue(formatMrfAmountWithCommas(amount), { emitEvent: false });
  }

  private resolveTeachingTypeForEdit(
    deptId: number,
    desigId: number,
    teachTypeIdHint: number | null = null
  ): Observable<{ typeId: number | string; label: string; departments: any[] } | null> {
    if (teachTypeIdHint != null) {
      const teachType = this.teachingTypeOptionsList.find(
        (t) => this.toNumber(t.typeId) === teachTypeIdHint
      );
      if (teachType?.typeId != null) {
        return this.hrHttpService.getMrfTypeWiseDesigAndDepartments(teachType.typeId).pipe(
          map((res) => ({
            typeId: teachType.typeId!,
            label: this.hrHttpService.getMrfTeachTypeLabel(teachType),
            departments: this.normalizeDepartments((res as { data?: unknown })?.data ?? res),
          })),
          catchError(() => this.scanTeachingTypesForEdit(deptId, desigId))
        );
      }
    }
    return this.scanTeachingTypesForEdit(deptId, desigId);
  }

  private scanTeachingTypesForEdit(
    deptId: number,
    desigId: number
  ): Observable<{ typeId: number | string; label: string; departments: any[] } | null> {
    const types = this.teachingTypeOptionsList.filter((t) => t.typeId != null);
    if (!types.length) {
      return of(null);
    }

    return forkJoin(
      types.map((teachType) =>
        this.hrHttpService.getMrfTypeWiseDesigAndDepartments(teachType.typeId!).pipe(
          map((res) => ({
            teachType,
            departments: this.normalizeDepartments((res as { data?: unknown })?.data ?? res),
          })),
          catchError(() => of({ teachType, departments: [] as any[] }))
        )
      )
    ).pipe(
      map((results) => {
        for (const { teachType, departments } of results) {
          const dept = departments.find(
            (d) => this.toNumber(d.deptId ?? d.departmentId ?? d.id) === deptId
          );
          if (!dept) {
            continue;
          }
          const desigs = this.normalizeDesignationList(dept.designations ?? dept.desigDetails ?? []);
          const desig = desigs.find(
            (d) => this.toNumber(d.desigId ?? d.designationId ?? d.id) === desigId
          );
          if (!desig) {
            continue;
          }
          return {
            typeId: teachType.typeId!,
            label: this.hrHttpService.getMrfTeachTypeLabel(teachType),
            departments,
          };
        }
        return null;
      })
    );
  }
}
